/**
 * Normalizes captured service data into reporter-consumable shapes.
 * Maps Appium command steps and assertion steps to core ActionStep format.
 */

import type { ActionStep, ActionCategory, ConsoleLogEntry, ConsoleLogLevel, CapturedNetworkRequest } from '@testrelic/core';
import type { AppiumCommandStep, AppiumAssertionStep, AppiumNetworkEntry, DeviceLogEntry } from './types.js';

function mapCommandCategory(category: string): ActionCategory {
  if (category === 'find' || category === 'interaction' || category === 'gesture' ||
      category === 'navigation' || category === 'lifecycle' || category === 'context' ||
      category === 'device' || category === 'keyboard' || category === 'alert' ||
      category === 'screenshot') {
    return 'ui_action';
  }
  return 'custom_step';
}

export function commandStepToAction(step: AppiumCommandStep): ActionStep {
  const title = step.selector
    ? `${step.command}(${step.selector})`
    : step.command;

  return {
    title,
    category: mapCommandCategory(step.category),
    timestamp: step.timestamp,
    duration: step.duration,
    videoOffset: null,
    status: step.result === 'success' ? 'passed' : 'failed',
    error: step.error ?? null,
    children: [],
  };
}

export function assertionStepToAction(step: AppiumAssertionStep): ActionStep {
  const title = step.expectedValue !== undefined
    ? `expect.${step.matcherName}(${JSON.stringify(step.expectedValue)})`
    : `expect.${step.matcherName}()`;

  return {
    title,
    category: 'assertion',
    timestamp: step.timestamp,
    duration: step.duration,
    videoOffset: null,
    status: step.passed ? 'passed' : 'failed',
    error: step.passed ? null : step.message,
    children: [],
  };
}

export function deviceLogToConsoleEntry(entry: DeviceLogEntry): ConsoleLogEntry {
  const levelMap: Record<string, ConsoleLogLevel> = {
    verbose: 'debug',
    debug: 'debug',
    info: 'info',
    warn: 'warn',
    error: 'error',
    fatal: 'error',
  };

  return {
    level: levelMap[entry.level] ?? 'info',
    text: entry.tag ? `[${entry.tag}] ${entry.message}` : entry.message,
    timestamp: entry.timestamp,
    location: null,
  };
}

export function networkEntryToRequest(entry: AppiumNetworkEntry): CapturedNetworkRequest {
  return {
    url: entry.url,
    method: entry.method,
    resourceType: 'xhr',
    statusCode: entry.status ?? 0,
    responseTimeMs: entry.duration ?? 0,
    startedAt: entry.timestamp,
    requestHeaders: entry.requestHeaders ?? null,
    requestBody: entry.requestBody ?? null,
    responseBody: entry.responseBody ?? null,
    responseHeaders: entry.responseHeaders ?? null,
    contentType: entry.mimeType ?? null,
    responseSize: entry.size ?? 0,
    requestBodyTruncated: false,
    responseBodyTruncated: false,
    isBinary: false,
    error: null,
  };
}
